import { Box, Flex, Image, Text, Tooltip } from '@chakra-ui/react';
import { WarningIcon, CheckCircleIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFilters } from '../../contexts/FilterContext';
import { DATE_MODES } from '../../utils/dateUtils';
import { formatPeriodRange, formatDate } from '../../utils/intlFormatters';
import { subAccountView, holdingsLine, brokerReturn } from './subAccountView';
import { statementLine } from '../../utils/statementFreshness';
import AccountSparkline from './AccountSparkline';
import DeltaPill from './DeltaPill';
import { TONE, LINE_TONE, toneOf } from './tones';
import RateSourceTooltip from '../shared/RateSourceTooltip';
import { balanceRateSources, holdsForeign } from '../../utils/rateSources';

// Preserve sign so an overdrawn asset reads "-N" instead of looking positive.
function formatNumber(n) {
  return Math.round(n || 0).toLocaleString('en-US');
}

const INVESTMENT_TYPES = new Set(['investment', 'stock', 'funds']);

/**
 * Right-hand cluster of a row: sparkline + delta pill + balance, for one
 * currency. `plain` forces balance-delta semantics (used by the multi-currency
 * "Todas" view, where each currency line is a plain balance, not investment ROI).
 */
// The currencies an account's balance is made of: its per-currency lines,
// else the ones it accepts.
const heldCurrencies = (account, perCurrencyStats) => (
  perCurrencyStats?.length ? perCurrencyStats.map(l => l.currency) : account?.currencies
);

/**
 * MI-329: whether an account's balance in the page's currency was converted —
 * it holds another currency. One that does not is exact, and is not marked "≈".
 */
export function accountIsConverted(account, perCurrencyStats, convert) {
  return Boolean(convert) && holdsForeign(heldCurrencies(account, perCurrencyStats), convert.target);
}

/** MI-329: the rates behind an account's converted balance — those of the currencies it holds. */
export function accountRateSources(account, perCurrencyStats, convert) {
  if (!convert) return [];
  return balanceRateSources(heldCurrencies(account, perCurrencyStats), convert.target, convert.sources);
}

function BalanceCluster({
  account, stats, roi, currency, approx = false, rateSources, plain = false, ownReturn = null,
}) {
  const balance = stats?.balance || 0;
  const sparkline = stats?.sparkline || [];

  // A broker that holds holdings shows its cash: its returns live in them.
  const isInvestment = !plain && INVESTMENT_TYPES.has(account.account_type)
    && !account.holdings_summary?.length;
  const investmentHasReturns = isInvestment && roi != null
    && ((roi.gains || 0) > 0 || (roi.losses || 0) > 0);

  // A broker with holdings: its return on them (`brokerReturn`), not the
  // change in its cash, which is what a broker's figure here is.
  const pillPct = ownReturn
    ? ownReturn.pct
    : isInvestment
      ? (investmentHasReturns ? roi?.lifetime_roi_pct : null)
      : stats?.delta_pct;
  let pillIsGood;
  let pillDirectionUp;
  if (ownReturn) {
    pillIsGood = Math.abs(pillPct) < 0.005 ? null : pillPct > 0;
    pillDirectionUp = pillPct > 0;
  } else if (isInvestment) {
    if (!investmentHasReturns || pillPct == null || Math.abs(pillPct) < 0.005) {
      pillIsGood = null;
      pillDirectionUp = false;
    } else {
      pillIsGood = pillPct > 0;
      pillDirectionUp = pillPct > 0;
    }
  } else {
    pillIsGood = stats?.delta_is_good;
    pillDirectionUp = (stats?.direction || 0) > 0;
  }
  const sparkColor = LINE_TONE[toneOf(pillIsGood)];

  return (
    <>
      <AccountSparkline values={sparkline} color={sparkColor} />
      {/* In `plain` (multi-currency) mode the pill sits in a fixed-width slot so
          the sparkline / pill / balance line up as columns across a row's
          currency lines. */}
      <Flex w={plain ? '86px' : undefined} justify="flex-end" flexShrink={0}>
        <Tooltip label={ownReturn?.note} isDisabled={!ownReturn?.note} hasArrow openDelay={300}>
          <Box as="span" display="inline-flex">
            <DeltaPill pct={pillIsGood == null ? null : pillPct} good={pillIsGood} up={pillDirectionUp} />
          </Box>
        </Tooltip>
      </Flex>
      <RateSourceTooltip sources={rateSources} isDisabled={!approx}>
      <Text
        fontSize="15px"
        fontWeight={600}
        minW={plain ? '132px' : undefined}
        // A gain reads green; a loss stays in ink, its pill already says so.
        color={
          investmentHasReturns
            ? (((roi.gains || 0) - (roi.losses || 0)) >= 0 ? 'redesign.amount.income' : 'redesign.heading')
            : 'redesign.heading'
        }
        textAlign="right"
        flexShrink={0}
        whiteSpace="nowrap"
      >
        {approx && <Text as="span" mr={0.5}>≈</Text>}
        <Text as="span" fontSize="10px" verticalAlign="0.35em" mr="3px" color="redesign.text.muted">{currency}</Text>
        {investmentHasReturns
          ? (() => {
              const netPL = (roi.gains || 0) - (roi.losses || 0);
              return `${netPL >= 0 ? '+' : '−'}${formatNumber(Math.abs(netPL))}`;
            })()
          : formatNumber(balance)}
      </Text>
      </RateSourceTooltip>
    </>
  );
}

// A sub-account figure: the cents go from four digits up, as on the phone.
function formatFigureValue(n) {
  const value = Number(n) || 0;
  return Math.abs(value) >= 1000
    ? formatNumber(value)
    : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Right-hand cluster of a holding, loan or person row: one line per currency
 * with the % pill (a holding's unrealized return, a loan's E.A.) and the
 * figure (`subAccountView`), marked "≈" when it was converted. The sparkline
 * stays the book balance, in the colour of the first line's return.
 */
function FigureCluster({ lines, sparkline, approx = false }) {
  return (
    <Flex direction="column" align="flex-end" gap={1.5} flexShrink={0}>
      {lines.map((l, i) => {
        const good = l.pct == null || Math.abs(l.pct) < 0.005 ? null : l.pct > 0;
        return (
          <Flex key={l.currency || i} align="center" gap={3}>
            <AccountSparkline values={i === 0 ? sparkline : []} color={LINE_TONE[toneOf(good)]} />
            <Tooltip label={l.pctNote} isDisabled={!l.pctNote} hasArrow openDelay={300}>
              <Box as="span" display="inline-flex">
                <DeltaPill pct={l.pct} good={good} up={Boolean(good)} suffix={l.pctSuffix || ''} />
              </Box>
            </Tooltip>
            <RateSourceTooltip sources={l.rateSources} isDisabled={!(approx && l.isConverted)}>
            <Text fontSize="15px" fontWeight={600} color="redesign.heading" textAlign="right" flexShrink={0} whiteSpace="nowrap">
              <Text as="span" fontSize="10px" verticalAlign="0.35em" mr="3px" color="redesign.text.muted">{approx && l.isConverted ? `≈ ${l.currency}` : l.currency}</Text>
              {formatFigureValue(l.value)}
            </Text>
            </RateSourceTooltip>
          </Flex>
        );
      })}
    </Flex>
  );
}

// Cap the "Off bank" tooltip to the newest N statements so a heavily-off account
// doesn't produce a wall of alerts; the rest surface as the newest ones are fixed.
const MAX_ALERTS = 3;

/**
 * Single row in the accounts list — shows icon, name, sparkline, % change, balance.
 * Click opens the edit detail panel (handled by parent).
 *
 * For investment accounts the % pill shows lifetime ROI (gain/loss as % of
 * principal invested) instead of period balance-delta — that's a more useful
 * signal for an investment than "did the cumulative balance move this month".
 *
 * A sub-account row (`nested`) sits indented under its parent; a parent row
 * with `childCount` children carries the toggle that folds them. A holding,
 * loan or person with loans shows its own figures (`subAccountView`), in the
 * page's display currency when `convert` names one.
 *
 * Line 2 says how far the account's statements go (`freshness`), else its
 * note or institution.
 */
export default function AccountRow({ account, stats, roi, currency, approx = false, convert = null, multiCurrency = false, perCurrencyStats, isSelected, onClick, nested = false, childCount = 0, expanded = true, onToggle, freshness }) {
  const { t } = useTranslation('accounts');
  const subView = subAccountView(account, t, convert);
  const statement = subView ? null : statementLine(freshness, t);
  const holdings = holdingsLine(account, t, convert);
  const navigate = useNavigate();
  const { clearFilters, addFilter, setDateMode } = useFilters();

  // MI-282 Rev2: open the movements view filtered to this account's movements
  // that need review (same FilterContext mechanism the dashboard uses, so the
  // filters actually stick across the navigation). The "alert" column with all
  // signals renders as the same labelled "Alert" chip used by the filter menu.
  // Must stay in step with the backend's MOVEMENT_REVIEW_FLAGS: the filter
  // validator rejects the whole request (400) on an unknown alert value.
  const REVIEW_FLAGS = [
    'is_incomplete', 'is_half_confirmed', 'needs_source_file', 'has_account_mismatch',
    'is_wrong_currency', 'is_exchange_missing', 'needs_source_rule_review',
  ];
  const openReview = (e) => {
    e.stopPropagation();
    clearFilters();
    // Show every date — the badge count spans all time, not the current window.
    setDateMode(DATE_MODES.ALL);
    addFilter({ entity: 'Movement', column: 'account_id', operator: 'in', values: [account.id] });
    addFilter({ entity: 'Movement', column: 'alert', operator: 'in', values: REVIEW_FLAGS });
    navigate('/movements');
  };
  const isInactive = account.is_active === false;

  return (
    <Box
      as="button"
      type="button"
      onClick={() => onClick(account)}
      w="100%"
      display="flex"
      alignItems={multiCurrency ? 'flex-start' : 'center'}
      px={2}
      pl={nested ? 10 : 2}
      py={nested ? 1.5 : '7px'}
      minH={nested ? undefined : '50px'}
      borderRadius="lg"
      gap={3}
      bg={isSelected ? 'redesign.bg.accent' : 'transparent'}
      _hover={{ bg: isSelected ? 'redesign.bg.accent' : 'redesign.bg.hover' }}
      textAlign="left"
      transition="background 0.15s"
      opacity={isInactive ? 0.45 : 1}
    >
      <Flex
        w="28px"
        h="28px"
        borderRadius="full"
        bg="redesign.bg.card"
        align="center"
        justify="center"
        flexShrink={0}
        fontSize="md"
        overflow="hidden"
      >
        {/* MI-304: the institution's logo identifies the account far better
            than a generic 🏦. Falls back to the account's own icon, which is
            what accounts with no source (cash, loans to people) rely on — and
            what shows if the asset fails to load. */}
        {account.source_logo_url ? (
          <Image
            src={account.source_logo_url}
            alt=""
            boxSize="18px"
            objectFit="contain"
            fallback={<Text fontSize="md">{account.icon || '🏦'}</Text>}
          />
        ) : (
          account.icon || '🏦'
        )}
      </Flex>

      <Box flex={1} textAlign="left" minW={0}>
        <Text fontSize="15px" fontWeight={500} color="redesign.heading" lineHeight="20px" isTruncated>
          {account.name}
          {account.external_account_number && (
            <Text as="span" color="redesign.text.muted" fontWeight="normal" ml="6px">
              {String(account.external_account_number).slice(-4)}
            </Text>
          )}
        </Text>
        {/* Copilot's second line: italic, muted, about how current the account is. */}
        <Text fontSize="13px" fontStyle="italic" lineHeight="18px" color={statement?.late ? 'redesign.text.warn' : 'redesign.text.muted'} isTruncated>
          {subView ? subView.subtitle : (statement?.text || account.note || account.source || t('row.manualAccount'))}
          {holdings && ` · ${holdings}`}
        </Text>
      </Box>

      {childCount > 0 && (
        <Flex
          as="span"
          role="button"
          aria-expanded={expanded}
          aria-label={t(expanded ? 'row.collapseChildren' : 'row.expandChildren', { name: account.name })}
          onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
          align="center"
          gap="2px"
          fontSize="2xs"
          fontWeight={600}
          px={1.5}
          py="2px"
          borderRadius="full"
          bg="redesign.bg.card"
          color="redesign.text.muted"
          flexShrink={0}
          cursor="pointer"
          _hover={{ color: 'redesign.heading' }}
        >
          <ChevronDownIcon boxSize={3} transform={expanded ? 'none' : 'rotate(-90deg)'} transition="transform 0.15s" />
          {childCount}
        </Flex>
      )}

      {account.needs_review_count > 0 && (
        <Tooltip
          label={t('row.toReview', { count: account.needs_review_count })}
          fontSize="xs"
          hasArrow
        >
          <Flex
            as="span"
            role="button"
            aria-label={t('row.toReview', { count: account.needs_review_count })}
            onClick={openReview}
            align="center"
            gap="2px"
            fontSize="2xs"
            fontWeight={600}
            px={1.5}
            py="2px"
            borderRadius="full"
            {...TONE.warn}
            flexShrink={0}
            cursor="pointer"
            _hover={{ bg: 'rgba(180, 83, 9, 0.20)', _dark: { bg: 'rgba(250, 204, 21, 0.24)' } }}
          >
            <WarningIcon boxSize="9px" />
            {account.needs_review_count}
          </Flex>
        </Tooltip>
      )}

      {account.reconciliation?.status === 'off' && (
        <Tooltip
          label={(() => {
            const gaps = account.reconciliation.gaps || [];
            const count = account.reconciliation.gap_count || gaps.length;
            const shown = gaps.slice(0, MAX_ALERTS);
            const extra = count - shown.length;
            return (
              <Box whiteSpace="pre-line">
                <Text fontWeight={600}>
                  {t(count === 1
                    ? 'row.reconcileOffHeaderOne'
                    : 'row.reconcileOffHeaderOther', { count })}
                </Text>
                {shown.map((g, i) => {
                  // Show both figures as magnitudes and let the numbers speak —
                  // no debt/asset logic. diff is computed from the displayed
                  // magnitudes so the shown numbers always add up.
                  const recordedMag = Math.abs(g.derived_balance || 0);
                  const bankMag = Math.abs(g.bank_balance || 0);
                  const recorded = `${g.currency} ${formatNumber(recordedMag)}`;
                  const bank = `${g.currency} ${formatNumber(bankMag)}`;
                  const diff = `${g.currency} ${formatNumber(Math.abs(recordedMag - bankMag))}`;
                  // Prefer the file name (the user's handle); else a period/date.
                  const label = g.filename || (g.period_start && g.period_end
                    ? formatPeriodRange(g.period_start, g.period_end)
                    : formatDate(g.as_of_date));
                  return (
                    <Text key={i}>
                      {t('row.reconcileOffLine', { label, recorded, bank, diff })}
                    </Text>
                  );
                })}
                {extra > 0 && (
                  <Text opacity={0.8}>{t('row.reconcileOffMore', { count: extra })}</Text>
                )}
              </Box>
            );
          })()}
          fontSize="xs"
          hasArrow
        >
          <Flex
            as="span"
            align="center"
            gap="2px"
            fontSize="2xs"
            fontWeight={600}
            px={1.5}
            py="2px"
            borderRadius="full"
            {...TONE.bad}
            flexShrink={0}
          >
            <WarningIcon boxSize="9px" />
            {t('row.offBank')}
          </Flex>
        </Tooltip>
      )}
      {account.reconciliation?.status === 'matches' && (
        <Tooltip label={t('row.reconcileMatches')} fontSize="xs" hasArrow>
          <Flex as="span" align="center" flexShrink={0} color="redesign.accent.green"
                aria-label={t('row.reconcileMatches')}>
            <CheckCircleIcon boxSize="11px" />
          </Flex>
        </Tooltip>
      )}

      {subView ? (
        <FigureCluster lines={subView.lines} sparkline={stats?.sparkline || []} approx={Boolean(convert)} />
      ) : multiCurrency ? (
        <Flex direction="column" align="flex-end" gap={1.5} flexShrink={0}>
          {(perCurrencyStats && perCurrencyStats.length > 0) ? (
            perCurrencyStats.map(line => (
              <Flex key={line.currency} align="center" gap={3}>
                <BalanceCluster account={account} stats={line} currency={line.currency} plain />
              </Flex>
            ))
          ) : (
            <Text fontSize="sm" color="redesign.text.muted">—</Text>
          )}
        </Flex>
      ) : (
        <BalanceCluster
          account={account} stats={stats} roi={roi} currency={currency}
          approx={approx && accountIsConverted(account, perCurrencyStats, convert)}
          rateSources={accountRateSources(account, perCurrencyStats, convert)}
          ownReturn={brokerReturn(account, t, convert)}
        />
      )}
    </Box>
  );
}
